import Vue from 'vue'
import { debounce } from 'util/ts-utils'
import domUtils from 'util/dom-utils'

import withRender from './layout.html'
import './masonry.scss'

/**
 * This component expects a "tilesAdded" event to be called when tiles have been added to it.
 * That kicks off setting tile width on the new tiles and positioning them in the layout.
 */
export default withRender((Vue as any).extend({
  name: 'MasonryLayout',
  data() {
    return {
      columnHeights: [],
      breakpoint: null,
      isRepositioning: false,
      maxColHeight: 0,
      numberOfColumns: 1,
      tileWidth: null,
      tileLeftPositions: []
    }
  },
  props: {
    breakpointsConfig: {
      type: Array,
      required: true
    },
    useProgressiveLoading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    layoutStyles() {
      // initial height of 200 will be adjusted as tiles are added
      return { height: `${this.maxColHeight || 200}px` }
    }
  },
  methods: {
    recalculate() {
      // this method determines numberOfColumns and tileWidth
      const containerWidth = this.$parent.$el.clientWidth
      // get the highest breakpoint where viewport is closest, but equal or above the
      // container width
      const bp = this.breakpointsConfig
        .filter(item => containerWidth >= item.viewport)
        .sort((a, b) => a.viewport > b.viewport ? -1 : 1)[0]
      this.breakpoint = bp

      if (bp.numOfCols) {
        // number of columns is specifically set.
        this.numberOfColumns = bp.numOfCols
      } else {
        // number of columns is not specified, it's based on minWidth...
        // In order to figure out the numberOfColumns (x), we need to account for the gutter size
        // being (numberOfColumns - 1) * hSpacing.
        // That produces the following equation:
        // x = (containerWidth - (spacing * (x - 1))) / minWidth
        // Solving for x (numberOfColumns) we get:
        this.numberOfColumns =
          Math.floor((containerWidth + bp.hSpacing) / (bp.minWidth + bp.hSpacing))
      }
      const gutterTotal = (this.numberOfColumns - 1) * bp.hSpacing
      const calcTileWidth = Math.floor((containerWidth - gutterTotal) / this.numberOfColumns)
      // if it chooses maxWidth, means it is too wide and will leave extra space on the right
      // side of the pinboard.
      // TODO: should we make the gutter wider in that case?
      const tileWidth = bp.maxWidth ? Math.min(calcTileWidth, bp.maxWidth) : calcTileWidth
      // If the tileWidth has not changed, there is nothing to do, return false to singify
      // it hasn't changed
      if (tileWidth === this.tileWidth) {
        return false
      }
      this.tileWidth = tileWidth

      // position of left-most column is always zero.  Only gutters between tiles
      this.tileLeftPositions = [0]
      this.columnHeights = [0]
      this.maxColHeight = 0
      for (let i = 1; i < this.numberOfColumns; i++) {
        const previousColLeft = this.tileLeftPositions[i - 1]
        this.tileLeftPositions.push(previousColLeft + this.tileWidth + bp.hSpacing)
        this.columnHeights.push(0) // initialize each column height to zero
      }
      // returning true to signify the tileWidth has changed
      return true
    },
    bindResize() {
      // remove event first so we don't accidentally add it twice
      window.removeEventListener('resize', this.onResize, false)
      window.addEventListener('resize', this.onResize, false)
    },
    bindScroll() {
      // make sure listener isn't already set
      this.removeScrollListener()
      if (this.useProgressiveLoading) {
        // load more on scroll to bottom
        window.addEventListener('scroll', this.onScroll, false)
      }
    },
    getMaxColHeight() {
      return this.columnHeights.reduce((max, val) => {
        return max > val ? max : val
      })
    },
    getMinHeightIndex() {
      let minIndex = 0
      for (let i = 1; i < this.columnHeights.length; i++) {
        if (this.columnHeights[minIndex] > this.columnHeights[i]) {
          minIndex = i
        }
      }
      return minIndex
    },
    onScroll: debounce(function cb() {
      // load more on scroll to bottom of the pinboard
      const scrollOffsetBottom = window.pageYOffset + window.innerHeight
      const pinboardRect = this.$el.getBoundingClientRect()
      if (scrollOffsetBottom >= pinboardRect.top + pinboardRect.height) {
        this.removeScrollListener()
        // we've reached the bottom, notify the component using this layout that it can load more
        this.$emit('loadMore')
      }
    }, 50),
    removeScrollListener() {
      window.removeEventListener('scroll', this.onScroll, false)
    },
    positionTile(tiles, tileIndex) {
      return new Promise((resolve, reject) => {
        if (!this.tileWidth || ! this.breakpoint) {
          // not initialized through recalculate() yet
          resolve()
          return
        }
        const tile = tiles[tileIndex]
        const colIndex = this.getMinHeightIndex()
        tile.tileAttributes.left = this.tileLeftPositions[colIndex]
        tile.tileAttributes.top = this.columnHeights[colIndex]

        // making sure the changes to tile are reflected in the UI before calculating height
        this.onTileReady(tile).then(() => {
          // waiting until the tile is ready to display it and determine it's height
          tile.tileAttributes.opacity = 1
          const newHeight =
            this.columnHeights[colIndex] + tile.$el.offsetHeight + this.breakpoint.vSpacing
          this.setNewColHeight(colIndex, newHeight)
          const nextTileIndex = tileIndex + 1
          if (tiles.length > nextTileIndex) {
            this.positionTile(tiles, nextTileIndex).then(() => {
              resolve()
            }).catch(() => {
              reject()
            })
          } else {
            // no more to position, go ahead and resolve
            resolve()
          }
        })
      })
    },
    // should only be called through on tilesAdded or onResize to help avoid too many calls
    positionTiles(fromResize) {
      this.isRepositioning = true
      try {
        const tiles = this.getSortedTiles()
        if (tiles.length > 0) {
          // if not resizing, just need to position the new tiles
          const tilesToPosition = fromResize ? tiles :
            tiles.filter(t => t.tileAttributes.opacity !== 1)
          // clear the current heights if resizing or all new tiles
          if (fromResize || tiles.length === tilesToPosition.length) {
            this.columnHeights.forEach((h, i) => this.columnHeights[i] = 0)
          }
          // positionTile is a recursive method so it can handle waiting for onTileReady before
          // positioning the next tile
          this.positionTile(tilesToPosition, 0).then(() => {
            // just in case the maxHeight is actually supposed to be less than it was before
            this.maxHeight = this.getMaxColHeight()
            // Notify the component using this layout, that it is done
            this.$emit('doneLoading')
            // once all tiles are positioned, we can allow positionTiles to be called again
            // when checking for isRepositioning
            this.isRepositioning = false
            // TT-11340. Just in case the user is already positioned at the bottom of the pinboard
            // with progressive loading on, see if more need to be loaded
            // $nextTick is used to wait for doneLoading to have time
            this.$nextTick(() => {
              if (this.useProgressiveLoading) {
                this.onScroll()
              }
            })
            // we can go ahead and bind the scroll event now that the tiles are all positioned
            this.bindScroll()
          }).catch(() => {
            // just in case
            this.isRepositioning = false
          })
        } else {
          this.isRepositioning = false
        }
      } catch (e) {
        this.isRepositioning = false
      }
    },
    pushColumnDown(tile) {
      // using the tile's left position, we can tell which column it is in, then we can push down
      // all the other tiles that are in the same column (based on their left as well).
      const colIndex = this.tileLeftPositions.indexOf(tile.tileAttributes.left)
      const columnHeightFromThisTile =
        tile.tileAttributes.top + tile.$el.offsetHeight + this.breakpoint.vSpacing
      const tiles = this.getSortedTiles()
      let heightDifference = 0 // the heightDifference is how much each tile needs to move down
      for (let i = (tile.tileOrder + 1); i < tiles.length; i++) {
        const nextTile = tiles[i]
        if (nextTile.tileAttributes.left === tile.tileAttributes.left) { // same column
          if (!heightDifference) {
            // haven't set it yet, gets set with first tile that needs to be pushed down
            heightDifference = columnHeightFromThisTile - nextTile.tileAttributes.top
          }
          nextTile.tileAttributes.top += heightDifference
        }
      }
      if (heightDifference) {
        this.setNewColHeight(colIndex, this.columnHeights[colIndex] + heightDifference)
      } else {
        // if heightDifference still hasn't been set, must be last tile in column
        this.setNewColHeight(colIndex, columnHeightFromThisTile)
      }
    },
    setNewColHeight(colIndex, newHeight) {
      this.columnHeights.splice(colIndex, 1, newHeight)
      const maxHeight = this.getMaxColHeight()
      if (this.maxColHeight < maxHeight) {
        this.maxColHeight = maxHeight
      }
    },
    getSortedTiles() {
      // put into new array so we don't sort this.$children. according to Vue documentation, the
      // order of $children is not guaranteed, so we have an "order" on each tile to keep them in
      // the correct order
      const tiles = this.$children.slice(0)
      tiles.sort((a, b) => a.tileOrder - b.tileOrder)
      return tiles
    },
    onResize: debounce(function cb() {
      if (this.recalculate()) {
        this.setWidthOnTiles()
        this.positionTiles(true)
      }
    }, 200),
    onTileReady(tile, attempts = 0) {
      // each tile has an isReady data element to signify it is ready to be drawn.
      // waiting for it to be true
      return new Promise((resolve, reject) => {
        if (tile.isReady || attempts > 20) {
          resolve()
          return
        }
        tile.$nextTick(() => {
          window.requestAnimationFrame(() => {
            this.onTileReady(tile, attempts + 1).then(() => resolve())
          })
        })
      })
    },
    setWidthOnTiles() {
      const tiles = this.getSortedTiles()
      for (let i = 0; i < tiles.length; i++) {
        tiles[i].setWidth(this.tileWidth)
      }
    }
  },
  created() {
    // this is called when tile have been added to the layout
    this.$on('tilesAdded', () => {
      if (this.tileWidth > 0) {
        this.setWidthOnTiles()
        this.positionTiles()
      }
    })
    this.$on('tileHeightChanged', (tile) => {
      tile.$nextTick(() => { // wait for the "expand" to complete
        // TODO: could just call reset() instead.  Only problem is seeing some tiles below
        // this one getting moved around, instead of just the current column being pushed down.
        // Would simplify the layout code.  Other issues?
        this.pushColumnDown(tile)
      })
    })
  },
  mounted() {
    domUtils.whenElementIsVisible(() => this.$el).then(() => {
      this.recalculate()
      this.bindScroll()
    })
    this.bindResize()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize, false)
    this.removeScrollListener()
  },
  watch: {
    useProgressiveLoading() {
      if (this.useProgressiveLoading) {
        this.bindScroll()
      } else {
        this.removeScrollListener()
      }
    }
  }
}))



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!(webpack)-strip-block!./src/layouts/masonry/layout.ts